// ════════════════════════════════════════════════════════════════════════════
// AdminMetricasTabV2 — refactor com Lpa primitivos
//
// Foco: 4 KPIs hero/lg + 2 charts via LpaChart. Substitui o legado que tinha
// 4+ charts grandes empilhados sem hierarquia.
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from "react";
import { LpaPageHeader, LpaSectionHeader, LpaKpiCard, LpaCard } from "../components/ui/lpa";
import LpaChart, { formatters } from "../components/charts/LpaChart";
import { fmtK, fmtP } from "./adminUtils.jsx";
import { snapToData } from "../shared/dataUtils.js";
import { fetchLatestPtax } from "../supabaseClient.js";
import { logWarn } from "../shared/logger.js";

interface ClientLite {
  id: string;
  full_name?: string;
  patrimonio?: { total?: number };
  feeAnual?: number;
  _snap?: unknown;
}

interface ThemeLike {
  card: string;
  text: string;
  muted: string;
  border: string;
  bg: string;
}

interface Props {
  T: ThemeLike;
  dynamicClients: ClientLite[];
}

export default function AdminMetricasTabV2({ T: _T, dynamicClients }: Props): React.ReactElement {
  const [ptaxBRL, setPtaxBRL] = useState(5.8);

  useEffect(() => {
    fetchLatestPtax(5.8)
      .then(setPtaxBRL)
      .catch((err) => logWarn("AdminMetricasTabV2", "PTAX fetch failed", { error: String(err) }));
  }, []);

  const totalAUM = dynamicClients.reduce((s, c) => s + (c.patrimonio?.total || 0), 0);

  const feeAnual = dynamicClients.reduce((s, c) => {
    const aum = c.patrimonio?.total || 0;
    const fee = c.feeAnual ?? 1.0;
    return s + aum * (fee / 100);
  }, 0);
  const feeMediaPct = totalAUM > 0 ? +((feeAnual / totalAUM) * 100).toFixed(2) : 1.0;

  // Performance consolidada (média ponderada)
  const weightedPerf = useMemo(() => {
    const clientsWithSnap = dynamicClients
      .map((c) => {
        const sd = c._snap ? (snapToData(c._snap) as Record<string, unknown>) : null;
        return {
          aum: c.patrimonio?.total || 0,
          perf: (sd?.perf as Record<string, number>) ?? null,
        };
      })
      .filter((x) => x.perf && x.aum > 0);

    const totalAUMWithSnap = clientsWithSnap.reduce((s, x) => s + x.aum, 0);
    if (!clientsWithSnap.length || totalAUMWithSnap <= 0) {
      return { acumulado: 0, ret12m: 0, retCDI12m: 0, pctCDI12m: 0 };
    }
    const w = (field: string) =>
      clientsWithSnap.reduce(
        (s, x) => s + (Number(x.perf?.[field]) || 0) * (x.aum / totalAUMWithSnap),
        0
      );
    return {
      acumulado: w("acumulado"),
      ret12m: w("ret12m"),
      retCDI12m: w("retCDI12m"),
      pctCDI12m: w("pctCDI12m"),
    };
  }, [dynamicClients]);

  // AUM history mock (em produção: time series do snapshot)
  const aumHistory = useMemo(() => {
    // Placeholder: gerar 12 meses sintéticos para demo. Em produção vem do DB.
    const months = [
      "Mai",
      "Jun",
      "Jul",
      "Ago",
      "Set",
      "Out",
      "Nov",
      "Dez",
      "Jan",
      "Fev",
      "Mar",
      "Abr",
    ];
    const baseFraction = 0.85;
    return months.map((m, i) => ({
      month: m,
      aum: totalAUM * (baseFraction + (i / months.length) * (1 - baseFraction)),
    }));
  }, [totalAUM]);

  return (
    <div style={{ padding: 0 }}>
      <LpaPageHeader
        title="Métricas consolidadas"
        sub={`${dynamicClients.length} clientes ativos · PTAX R$ ${ptaxBRL.toFixed(2)}`}
      />

      {/* KPIs hero */}
      <LpaSectionHeader title="Visão consolidada" sub="Médias ponderadas pelo AUM" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <LpaKpiCard
          label="AUM total"
          value={`R$ ${fmtK(totalAUM)}`}
          delta={{ value: 2.3, suffix: "% MoM", direction: "up" }}
          size="hero"
          hint="Soma do patrimônio total de todos os clientes ativos"
        />
        <LpaKpiCard
          label="Fee anual projetado"
          value={`R$ ${fmtK(feeAnual)}`}
          size="lg"
          hint={`Média: ${feeMediaPct}% a.a.`}
        />
        <LpaKpiCard label="Clientes ativos" value={dynamicClients.length} size="lg" />
      </div>

      {/* KPIs operacionais */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <LpaKpiCard
          label="Retorno acumulado"
          value={fmtP(weightedPerf.acumulado)}
          valueColor={
            weightedPerf.acumulado > 0
              ? "var(--lpa-success)"
              : weightedPerf.acumulado < 0
                ? "var(--lpa-danger)"
                : undefined
          }
          size="md"
        />
        <LpaKpiCard
          label="Retorno 12m"
          value={fmtP(weightedPerf.ret12m)}
          valueColor={
            weightedPerf.ret12m > 0
              ? "var(--lpa-success)"
              : weightedPerf.ret12m < 0
                ? "var(--lpa-danger)"
                : undefined
          }
          size="md"
        />
        <LpaKpiCard label="CDI 12m" value={fmtP(weightedPerf.retCDI12m)} size="md" />
        <LpaKpiCard
          label="% CDI capturado"
          value={fmtP(weightedPerf.pctCDI12m)}
          size="md"
          hint="Quanto do CDI suas carteiras agregadas capturaram nos últimos 12 meses"
        />
      </div>

      {/* Histórico AUM */}
      <LpaSectionHeader title="Histórico de AUM" sub="Últimos 12 meses" />
      <LpaCard variant="elevated" style={{ marginBottom: 16 }}>
        <LpaChart
          kind="area"
          data={aumHistory as unknown as Array<Record<string, unknown>>}
          xKey="month"
          height={260}
          series={[{ key: "aum", label: "AUM total", color: "var(--lpa-accent)" }]}
          yFormatter={(v) => `R$ ${formatters.compact(v as number)}`}
        />
      </LpaCard>
    </div>
  );
}
